import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Users, Facebook, Instagram, Linkedin } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLACEHOLDER_MEMBERS = [
  {
    id: '1',
    name: 'Carlo Ferrara',
    role: { it: 'Presidente', en: 'President', fr: 'Président', de: 'Präsident', es: 'Presidente' },
    bio: { it: 'Appassionato di arrampicata dal 1995, fondatore dell\'associazione.', en: 'Climbing enthusiast since 1995, founder of the association.', fr: 'Passionné d\'escalade depuis 1995, fondateur de l\'association.', de: 'Kletterbegeisterter seit 1995, Gründer des Vereins.', es: 'Apasionado de la escalada desde 1995, fundador de la asociación.' },
    photo_url: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400',
    social_links: { facebook: '#', instagram: '#' }
  },
  {
    id: '2',
    name: 'Marco Rossi',
    role: { it: 'Vice Presidente', en: 'Vice President', fr: 'Vice-Président', de: 'Vizepräsident', es: 'Vicepresidente' },
    bio: { it: 'Istruttore FASI, responsabile della chiodatura.', en: 'FASI instructor, bolting manager.', fr: 'Instructeur FASI, responsable de l\'équipement.', de: 'FASI-Trainer, Verantwortlicher für die Bohrungen.', es: 'Instructor FASI, responsable de equipamiento.' },
    photo_url: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=400',
    social_links: { instagram: '#' }
  },
  {
    id: '3',
    name: 'Giulia Bianchi',
    role: { it: 'Segretaria', en: 'Secretary', fr: 'Secrétaire', de: 'Sekretärin', es: 'Secretaria' },
    bio: { it: 'Coordinatrice degli eventi e delle attività sociali.', en: 'Events and social activities coordinator.', fr: 'Coordinatrice des événements et des activités sociales.', de: 'Koordinatorin für Veranstaltungen und soziale Aktivitäten.', es: 'Coordinadora de eventos y actividades sociales.' },
    photo_url: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=400',
    social_links: { linkedin: '#', instagram: '#' }
  }
];

export function AboutPage() {
  const { t, getTranslatedContent, language } = useLanguage();
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTeam = async () => {
      try {
        const res = await axios.get(`${API}/team`);
        setTeamMembers(res.data || []);
      } catch (error) {
        console.error('Error fetching team:', error);
        setTeamMembers([]);
      } finally {
        setLoading(false);
      }
    };
    fetchTeam();
  }, []);

  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section className="relative py-32 bg-[#0A0A0A]">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Users className="w-16 h-16 text-[#FF6D00] mx-auto mb-6" />
            <h1 className="heading-xl text-white mb-6">
              {t('aboutUs')}
            </h1>
            <p className="body-lg text-gray-400 max-w-2xl mx-auto">
              {language === 'it'
                ? 'Scopri chi siamo e la nostra passione per l\'arrampicata in Sicilia.'
                : 'Discover who we are and our passion for climbing in Sicily.'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* About Section */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="caption mb-4 block">Caltavuturo Climbing</span>
              <h2 className="heading-lg text-[#0A0A0A] mb-6">
                {language === 'it' ? 'La nostra storia' : 'Our story'}
              </h2>
              <div className="space-y-4 text-gray-600 whitespace-pre-line">
                {t('climbingHistoryText')}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_sport-climb-cms/artifacts/t2zfhsiq_herosection.jpg"
                alt="Team climbing"
                className="w-full rounded-lg shadow-2xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="caption mb-4 block">{t('team')}</span>
            <h2 className="heading-lg text-[#0A0A0A]">
              {language === 'it' ? 'Il nostro team' : 'Our team'}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {teamMembers.map((member, i) => (
              <motion.div
                key={member.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white rounded-lg overflow-hidden group"
              >
                <div className="relative aspect-[4/5] overflow-hidden">
                  <img 
                    src={member.photo_url || 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400'}
                    alt={member.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  
                  {/* Social Links */}
                  {member.social_links && Object.keys(member.social_links).length > 0 && (
                    <div className="absolute bottom-4 left-4 flex gap-2">
                      {member.social_links.facebook && (
                        <a href={member.social_links.facebook} className="w-10 h-10 bg-white/90 rounded-full flex items-center justify-center hover:bg-[#FF6D00] hover:text-white transition-colors">
                          <Facebook className="w-5 h-5" />
                        </a>
                      )}
                      {member.social_links.instagram && (
                        <a href={member.social_links.instagram} className="w-10 h-10 bg-white/90 rounded-full flex items-center justify-center hover:bg-[#FF6D00] hover:text-white transition-colors">
                          <Instagram className="w-5 h-5" />
                        </a>
                      )}
                      {member.social_links.linkedin && (
                        <a href={member.social_links.linkedin} className="w-10 h-10 bg-white/90 rounded-full flex items-center justify-center hover:bg-[#FF6D00] hover:text-white transition-colors">
                          <Linkedin className="w-5 h-5" />
                        </a>
                      )}
                    </div>
                  )}
                </div>
                <div className="p-6">
                  <h3 className="font-heading text-xl uppercase mb-1">{member.name}</h3>
                  <p className="text-[#FF6D00] text-sm font-semibold mb-3">
                    {getTranslatedContent(member.role)}
                  </p>
                  <p className="text-gray-600 text-sm">
                    {getTranslatedContent(member.bio)}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="caption mb-4 block">{language === 'it' ? 'I nostri valori' : 'Our values'}</span>
            <h2 className="heading-lg text-[#0A0A0A]">
              {language === 'it' ? 'Cosa ci guida' : 'What guides us'}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: language === 'it' ? 'Sicurezza' : 'Safety',
                desc: language === 'it' 
                  ? 'La sicurezza è la nostra priorità. Manteniamo le vie sempre in condizioni ottimali.'
                  : 'Safety is our priority. We keep routes in optimal condition at all times.'
              },
              {
                title: language === 'it' ? 'Comunità' : 'Community',
                desc: language === 'it'
                  ? 'Crediamo in una comunità inclusiva dove tutti sono i benvenuti.'
                  : 'We believe in an inclusive community where everyone is welcome.'
              },
              {
                title: language === 'it' ? 'Ambiente' : 'Environment',
                desc: language === 'it'
                  ? 'Rispettiamo e proteggiamo l\'ambiente naturale delle nostre falesie.'
                  : 'We respect and protect the natural environment of our crags.'
              }
            ].map((value, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="text-center p-8 border rounded-lg hover:border-[#FF6D00]/50 transition-colors"
              >
                <h3 className="font-heading text-2xl uppercase mb-4">{value.title}</h3>
                <p className="text-gray-600">{value.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
